import { forwardRef, useEffect, useMemo } from 'react';
import {
  Link,
  NavigateFunction,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { resetOpenCall, useOpenCalls } from '@/state';
import {
  type OpenCallBodyCell,
  OpenCallsPagination,
  OpenCallsTable,
  type OpenCallTableCell,
} from '@repo/ui/components/custom';
import { GetRowCTAResult } from '@repo/ui/components/custom/open-calls-table/component';
import { Badge } from '@repo/ui/components/ui/badge';
import { buttonVariants } from '@repo/ui/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { useScreenSize } from '@repo/ui/hooks';
import { cn } from '@repo/ui/lib/utils';
import { OpenCallModel } from '@repo/ui/models/index';

const tableHeaderCells = (openCalls: OpenCallModel[]) => {
  const headers = [
    {
      props: {
        className: 'w-16 sm:w-24 sm:font-bold text-sm pl-4',
      },
      children: <>Brand</>,
    },
    {
      props: {
        className: 'sm:font-bold text-sm pl-4',
      },
      children: (
        <>
          <span className="hidden xl:inline">Open Call</span> Name
        </>
      ),
    },
    {
      props: {
        className: 'sm:font-bold text-sm pl-4',
      },
      children: (
        <>
          <span className="hidden xl:inline">Application</span> Status
        </>
      ),
    },
    {
      hide: openCalls?.every((x) => x.totalApplicants === null),
      props: {
        className: 'sm:font-bold text-sm pl-4 hidden sm:table-cell',
      },
      children: <>Total Applicants</>,
    },
    {
      props: {
        className: 'sm:font-bold text-sm pl-4 hidden lg:table-cell',
      },
      children: <>Created Date</>,
    },
    {
      props: {
        className: 'sm:font-bold text-sm pl-4 hidden md:table-cell',
      },
      children: <>Closed Date</>,
    },
    {
      props: {
        className: 'sm:font-bold text-sm pl-4',
      },
      // Empty header cell for CTAs
      children: <></>,
    },
  ];
  return headers.filter((x) => !x.hide);
};

const OpenCallLink = forwardRef<
  HTMLAnchorElement,
  {
    openCall: OpenCallModel;
    children: React.ReactNode;
    className?: string;
    dashboardPath: string;
  }
>(({ openCall, children, className, dashboardPath, ...props }, ref) => {
  return (
    <Link
      ref={ref}
      to={`/open-calls/${openCall.id}/stage/${openCall.defaultStageId}`}
      state={{ dashboardPath }}
      className={cn(className)}
      {...props}
    >
      {children}
    </Link>
  );
});

OpenCallLink.displayName = 'OpenCallLink';

const tableBodyCells = (
  openCalls: OpenCallModel[],
  navigate: NavigateFunction,
  dashboardPath: string,
): BaseCell[] => {
  const body = [
    {
      props: {
        className: 'pl-4 h-full',
      },
      children: (openCall: OpenCallModel) => (
        <img
          src={openCall.brand.logoURL as string | undefined}
          alt="Vogue logo"
          className="w-10 h-10 sm:w-16 sm:h-16 rounded-md border"
        />
      ),
    },
    {
      props: {
        className: 'pl-4 h-full',
      },
      children: (openCall: OpenCallModel) => (
        <p className="truncate w-24 lg:w-40 overflow-hidden">{openCall.name}</p>
      ),
    },
    {
      props: {
        className: 'pl-4 h-full',
      },
      children: (openCall: OpenCallModel) => (
        <Badge
          variant={`${openCall.isLive ? 'outline' : 'secondary'}`}
          className={cn('capitalize rounded-full', {
            'bg-cn-secondary': !openCall.isLive,
          })}
        >
          {openCall.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      hide: openCalls?.every((x) => x.totalApplicants === null),
      props: {
        className: 'hidden sm:table-cell pl-4',
      },
      children: (openCall: OpenCallModel) => (
        <>{openCall.totalApplicants?.toLocaleString()}</>
      ),
    },
    {
      props: {
        className: 'hidden lg:table-cell pl-4',
      },
      children: (openCall: OpenCallModel) => (
        <>{openCall.createdAt as string}</>
      ),
    },
    {
      props: {
        className: 'hidden md:table-cell pl-4',
      },
      children: (openCall: OpenCallModel) => <>{openCall.closedAt as string}</>,
    },
    {
      children: (openCall: OpenCallModel) => (
        <button
          onClick={(e) => {
            e.preventDefault(); // Prevent bubbling to parent link
            e.stopPropagation();
            navigate(
              `/open-calls/${openCall.id}/stage/${openCall.defaultStageId}`,
              { state: { dashboardPath } },
            );
          }}
          className={cn(
            `${buttonVariants({ variant: 'outline' })} sm:font-bold`,
          )}
        >
          Enter
        </button>
      ),
    },
  ];
  return body.filter((x) => !x.hide);
};
type BaseCell = OpenCallTableCell | OpenCallBodyCell;
const getTableCells = (tableCells: BaseCell[]) => {
  return tableCells.map(
    (cell) =>
      ({
        ...cell,
        props: cell?.props,
        children: cell.children,
      }) satisfies BaseCell,
  );
};

const getRowCTA = (
  openCall: OpenCallModel,
  dashboardPath: string,
): GetRowCTAResult => {
  const { isSmall } = useScreenSize();
  const rowHeight = isSmall ? 'h-[56px]' : 'h-[95px]';

  return {
    Wrapper: ({ children }) => (
      <TooltipProvider delayDuration={500}>
        <Tooltip>
          <TooltipTrigger asChild>
            <OpenCallLink
              openCall={openCall}
              dashboardPath={dashboardPath}
              className={`p-2 block w-full ${rowHeight} flex items-center`}
            >
              {children}
            </OpenCallLink>
          </TooltipTrigger>
          <TooltipContent
            sticky="always"
            className="bg-background p-2 px-4 max-w-[22.6875rem] text-sm font-normal text-cn-foreground shadow-lg rounded-md"
          >
            <p>{openCall.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    isClickable: true,
  };
};

export const Dashboard = () => {
  const { openCalls, fetchOpenCalls, loading, totalCount } = useOpenCalls();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number(searchParams.get('page'));
  const currentPage =
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const limit = 10;
  const dashboardPath = currentPage > 1 ? `/?page=${currentPage}` : '/';

  useEffect(() => {
    resetOpenCall();
  }, []);

  const { headerCells, bodyCells } = useMemo(() => {
    return {
      headerCells: getTableCells(tableHeaderCells(openCalls)),
      bodyCells: getTableCells(
        tableBodyCells(openCalls, navigate, dashboardPath),
      ),
    };
  }, [dashboardPath, navigate, openCalls]);

  useEffect(() => {
    fetchOpenCalls(currentPage, limit);
  }, [currentPage, fetchOpenCalls]);

  useEffect(() => {
    const pageCount = Math.ceil(totalCount / limit);
    if (!loading && pageCount > 0 && currentPage > pageCount) {
      setSearchParams(pageCount === 1 ? {} : { page: String(pageCount) });
    }
  }, [currentPage, loading, setSearchParams, totalCount]);

  return (
    <section className="px-2 py-4 md:px-6 h-screen">
      <section className="flex flex-row items-center px-4 md:px-0 mb-3 md:mb-0">
        <img
          src="/images/ttt-logo.svg"
          alt="Talent Tracker Tool - Logo"
          className="text-cn-muted-foreground w-5 h-5 mr-2.5"
        />
        <h2 className="font-bold text-sm text-cn-muted-foreground">
          Condé Nast Talent Tracker
        </h2>
        <Link
          to="/photovogue"
          className="ml-auto text-sm font-medium text-cn-muted-foreground underline-offset-4 hover:text-cn-foreground hover:underline"
        >
          PhotoVogue admin →
        </Link>
      </section>

      <hr className="bg-cn-border -mx-2 md:hidden" />

      <section className="mt-3 md:mt-6 py-6 md:px-7 bg-white rounded-lg border">
        <section className="px-4 md:px-0">
          <h1 className="font-semibold text-2xl tracking-tight">
            Open Call Dashboard
          </h1>
          <span className="mt-1.5 text-sm text-cn-muted-foreground">
            View the current open calls and manage new talent.
          </span>
        </section>

        <OpenCallsTable
          loading={loading}
          openCalls={openCalls}
          tableProps={{
            className: 'mt-6',
          }}
          headerCells={headerCells as OpenCallTableCell[]}
          bodyCells={loading ? [] : bodyCells}
          getRowCTA={(openCall) => getRowCTA(openCall, dashboardPath)}
          totalCount={totalCount}
          page={currentPage}
          limit={limit}
        />
        <OpenCallsPagination
          page={currentPage}
          limit={limit}
          totalCount={totalCount}
          loading={loading}
          onPageChange={(page) =>
            setSearchParams(page === 1 ? {} : { page: String(page) })
          }
        />
      </section>
    </section>
  );
};
